import { GraviGrid, Horizontal, RangePicker, Texto } from '@gravitate-js/excalibrr'
import React, { useState } from 'react'

import { useInvoices } from '../../../../api/useFreight/useInvoices'
import { getTransactionReportColumnDefs } from '.'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'

type UndefinedDate = Date | undefined

const STORAGE_KEY = 'freight-transactions-report'

export function TransactionsReportPage() {
  const { updateSetting } = useUserSettings()
  // Initialize with last 7 days
  const [dateRangeFilter, setDateRangeFilter] = useState<[UndefinedDate, UndefinedDate]>(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 30)
    return [start, end]
  })

  const { useInvoicesQuery } = useInvoices()
  const { data: invoicesData, isLoading } = useInvoicesQuery(
    dateRangeFilter[0] && dateRangeFilter[1]
      ? {
          start_date: dateRangeFilter[0],
          end_date: dateRangeFilter[1],
        }
      : undefined
  )

  // Flatten and hydrate the data structure
  const flattenedData = React.useMemo(() => {
    if (!invoicesData?.rows) return []

    return invoicesData.rows.flatMap((group) =>
      group.invoices
        .filter((invoice) => invoice.status === 'sent')
        .flatMap((invoice) =>
          invoice.transactions.flatMap((transaction) =>
            transaction.details.map((detail) => ({
              // Invoice Group Level
              group_id: group.id,
              group_number: group.group_number,
              group_status: group.status,
              counterparty_name: group.counterparty_name,
              group_start_date: group.start_date,
              group_end_date: group.end_date,
              group_order_count: group.order_count,
              group_base_amount: group.base_amount,
              group_accessorial_amount: group.accessorial_amount,
              group_surcharge_amount: group.surcharge_amount,
              group_total_amount: group.total_amount,
              group_updated_date: group.updated_date,
              group_created_by: group.created_by,
              group_updated_by: group.updated_by,

              // Invoice Level
              invoice_id: invoice.id,
              invoice_number: invoice.invoice_number,
              invoice_type: invoice.type,
              invoice_status: invoice.status,
              invoice_date: invoice.invoice_date,
              invoice_sent_date: invoice.sent_date,
              invoice_base_amount: invoice.base_amount,
              invoice_accessorial_amount: invoice.accessorial_amount,
              invoice_surcharge_amount: invoice.surcharge_amount,
              invoice_total_amount: invoice.total_amount,

              // Transaction (Order) Level
              order_id: transaction.order_id,
              carrier_id: transaction.carrier_id,
              carrier_name: transaction.carrier_name,
              customer_id: transaction.customer_id,
              customer_name: transaction.customer_name,
              destinations: transaction.destinations,
              transaction_status: transaction.status,
              product_groups: transaction.product_groups,
              distance: transaction.distance,
              distance_uom: transaction.distance_uom,
              volume: transaction.volume,
              volume_uom: transaction.volume_uom,
              transaction_total: transaction.total,
              transaction_accessorial_total: transaction.accessorial_total,
              transaction_surcharge_total: transaction.surcharge_total,
              transaction_base_total: transaction.base_total,

              // Transaction Detail Level
              ...detail,
            }))
          )
        )
    )
  }, [invoicesData])

  return (
    <GraviGrid
      primaryKey='id'
      rowData={flattenedData}
      columnDefs={getTransactionReportColumnDefs()}
      storageKey={STORAGE_KEY}
      loading={isLoading}
      controlBarProps={{
        title: 'Freight Transactions Report',
        hideActiveFilters: false,
        actionButtons: (
          <Horizontal verticalCenter gap={8}>
            <Texto appearance='hint'>Date Range: </Texto>
            <RangePicker
              inputKey='date-range'
              dates={dateRangeFilter}
              onChange={setDateRangeFilter}
              placement='bottomLeft'
            />
          </Horizontal>
        ),
      }}
      agPropOverrides={{
        domLayout: 'normal',
        rowGroupPanelShow: 'always',
        groupDisplayType: 'groupRows',
        suppressRowTransform: true,
        rowSelection: 'multiple',
        enableRangeSelection: true,
        rowHeight: 40,
        getRowId: (params) => params.data.id,
        animateRows: true,
        suppressMaintainUnsortedOrder: false,
      }}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
    />
  )
}
